import type React from "react";
import { useContext, useState } from "react";

import { faFilesMedical } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { useTheme } from "styled-components";

import { useGetHealthReportQuery } from "api/hooks/useGetHealthReportsQuery";
import { useGetPatientHealthJournalQuery } from "api/hooks/useGetPatientHealthJournalQuery";
import type { HealthReport } from "api/models/HealthReport";
import { Notification } from "shared/atoms/Notification";
import { CurrentPatientContext } from "utils/contexts";
import { translatePainLocation } from "utils/translations";

import { HealthReportsHeader } from "../HealthReportsHeader";
import { questions } from "../helpers";

import { HealthReportsGrid, HealthReportsGridSkeleton } from "./HealthReportsGrid";

const sortByNumber = (report1: HealthReport, report2: HealthReport) => {
  if (report1.health_report_number < report2.health_report_number) return 1;
  if (report1.health_report_number > report2.health_report_number) return -1;
  return 0;
};

export const HealthReports: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { patient, previousTreatmentId } = useContext(CurrentPatientContext);
  const { patientId } = useParams<{ patientId: string }>();
  const [selected, setSelected] = useState<HealthReport[]>([]);
  const [hidden, setHidden] = useState(false);

  const { data: healthJournal } = useGetPatientHealthJournalQuery({
    patientId: patient.id,
    treatment_id: previousTreatmentId,
  });
  const {
    data: reports = [],
    error,
    isLoading,
  } = useGetHealthReportQuery(
    { patientId: patient.id, treatment_id: previousTreatmentId },
    {
      onSettled(data) {
        if (data && data.length > 0) {
          switch (data.length) {
            case 0:
              setSelected([]);
              break;
            case 1:
              setSelected([data[0]]);
              break;
            default:
              setSelected([data[data.length - 1], data[data.length - 2]].sort(sortByNumber));
          }
        }
      },
    }
  );

  const lateralLocation = patient?.most_painful_location?.lateral_location;
  const ailment = healthJournal?.ailment || "joint_pain";

  const painLocation = t("patients.pain_construct", {
    // FIXME: type translation
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    lateral: lateralLocation !== "undefined" ? t(`patients.lateral_location.${lateralLocation}`) : "",
    location: translatePainLocation({
      painLocation: patient?.most_painful_location?.location || "",
      plural: lateralLocation === "both",
    }),
  }).toLowerCase();
  const joint = translatePainLocation({ painLocation: patient?.most_painful_location?.location || "" }).toLowerCase();

  const createIdList = () => {
    return selected.map(select => select.id);
  };

  const changeCallback = (selectedIds: number[]) => {
    const selectedReports = selectedIds.reduce((list, select) => {
      const report = reports.find(el => el.id === select);
      return report ? [...list, report] : list;
    }, [] as HealthReport[]);
    setSelected(selectedReports.sort(sortByNumber) || []);
  };

  const hideRowsCallback = (hide: boolean) => {
    setHidden(hide);
  };

  return (
    <>
      <HealthReportsHeader
        selected={createIdList()}
        reportList={reports}
        changeCallback={changeCallback}
        rowsCallback={hideRowsCallback}
      />
      {!isLoading && reports && reports.length === 0 && (
        <Stack spacing={2} my={5} sx={{ alignItems: "center", justifyContent: "center" }}>
          <FontAwesomeIcon icon={faFilesMedical} size="3x" color={theme.colors.redesign.db40} />
          <Typography color="textSecondary">{t("patients.health_reports.no_reports_detail")}</Typography>
          <Typography color="textSecondary">
            {t("patients.health_reports.no_reports_detail_legacy_hint")}{" "}
            <Link to={`/patients/${patientId}/information/health-reports-v2`}>{t("patients.health_reports.here")}</Link>
            .
          </Typography>
        </Stack>
      )}
      {!isLoading && reports && reports.length !== 0 && (
        <HealthReportsGrid
          selectedHealthReports={selected}
          questions={questions(painLocation, joint, ailment)}
          hideUnmodifiedRows={hidden}
          painLocation={patient?.most_painful_location?.location}
        />
      )}
      {isLoading && <HealthReportsGridSkeleton columnCount={2} rowCount={10} />}
      {error && (
        <Box my={5} mx="auto" maxWidth={400}>
          <Notification variant="danger">{t("errors.generic")}</Notification>
        </Box>
      )}
    </>
  );
};
